import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  AssistantPersonName,
  type AssistantLink,
  type AssistantLinkCodeResponse,
} from '@aeris/contracts'
import {
  Button,
  ConfirmDialog,
  Field,
  Input,
  QueryState,
  Section,
  SwitchField,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  fetchMyAssistantWhatsapp,
  renameMyAssistantWhatsapp,
  requestAssistantLinkCode,
  setMyAssistantDigest,
  unlinkMyAssistantWhatsapp,
} from '../lib/api.ts'
import { formatDayTime } from '../lib/date-format.ts'

/** "12:00" → "12h"; "18:30" → "18h30". */
const hourLabel = (time: string): string =>
  time.endsWith(':00')
    ? `${String(Number(time.slice(0, 2)))}h`
    : `${String(Number(time.slice(0, 2)))}h${time.slice(3)}`

/**
 * O meu WhatsApp no assistente (F254, F273).
 *
 * "Ligar meu WhatsApp" gera um código de 8 dígitos (15 minutos, uma vez) e um
 * botão que abre o WhatsApp no número do assistente com o código escrito. A
 * pessoa só aperta enviar: o código prova que o WhatsApp é dela, e é por ele
 * que o Aeris descobre o LID de quem escreve (ADR-0039).
 *
 * F273: a pessoa liga até `maxLinks` números (o dono: "uns 3 no admin"). Cada
 * um tem o seu resumo do dia e o seu "desligar".
 *
 * F276: a conta admin é usada por mais de uma pessoa. Antes do código vem o
 * nome de quem vai usar o número — obrigatório, como o "Nome" do convite —, e
 * o assistente chama cada um pelo seu nome. "Trocar o nome" corrige depois.
 */

/** A mesma regra do servidor: aparado, obrigatório, até 200. */
const nameError = (value: string): string | undefined => {
  const parsed = AssistantPersonName.safeParse(value)
  return parsed.success ? undefined : parsed.error.issues[0]?.message
}
export const MyAssistantWhatsapp = (): ReactNode => {
  const queryClient = useQueryClient()
  const [code, setCode] = useState<AssistantLinkCodeResponse | null>(null)
  /** Quantos estavam ligados quando o código saiu: um a mais, chegou. */
  const [linkedAtCode, setLinkedAtCode] = useState(0)
  const status = useQuery({
    queryKey: ['my-assistant-whatsapp'],
    queryFn: fetchMyAssistantWhatsapp,
    // Enquanto há um código na tela, confere se a pessoa já mandou.
    refetchInterval: code === null ? false : 5_000,
  })
  const [unlinking, setUnlinking] = useState<AssistantLink | null>(null)
  /** O nome de quem vai usar o próximo número; nulo: o formulário fechado. */
  const [newName, setNewName] = useState<string | null>(null)
  const [newNameTouched, setNewNameTouched] = useState(false)
  /** O número cujo nome está sendo trocado, e o texto. */
  const [renaming, setRenaming] = useState<{
    readonly id: string
    readonly name: string
  } | null>(null)

  const links = status.data?.links ?? []
  // O código chegou: o número novo já está na lista.
  if (code !== null && links.length > linkedAtCode) {
    setCode(null)
    setNewName(null)
  }

  const issue = useMutation({
    mutationFn: async (personName: string) =>
      await requestAssistantLinkCode(personName),
    onSuccess: (issued) => {
      setLinkedAtCode(links.length)
      setCode(issued)
    },
    onError: (cause) => {
      notify.error(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível gerar o código.'
      )
    },
  })
  const digest = useMutation({
    mutationFn: async (input: { linkId: string; on: boolean }) =>
      await setMyAssistantDigest(input.linkId, input.on),
    onSuccess: (data, input) => {
      queryClient.setQueryData(['my-assistant-whatsapp'], data)
      notify.success(
        input.on
          ? 'Este número volta a receber o resumo do dia.'
          : 'Este número não recebe mais o resumo do dia.'
      )
    },
  })
  const rename = useMutation({
    mutationFn: async (input: { linkId: string; name: string }) =>
      await renameMyAssistantWhatsapp(input.linkId, input.name.trim()),
    onSuccess: (data) => {
      queryClient.setQueryData(['my-assistant-whatsapp'], data)
      setRenaming(null)
      notify.success('Nome trocado. O assistente já chama assim.')
    },
    onError: (cause) => {
      notify.error(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível trocar o nome.'
      )
    },
  })
  const unlink = useMutation({
    mutationFn: async (linkId: string) => {
      await unlinkMyAssistantWhatsapp(linkId)
    },
    onSuccess: async () => {
      setUnlinking(null)
      notify.success('O número foi desligado do assistente.')
      await queryClient.invalidateQueries({
        queryKey: ['my-assistant-whatsapp'],
      })
    },
  })

  const data = status.data
  const max = data?.maxLinks ?? 1
  const full = links.length >= max

  const codePanel =
    code === null || data === undefined ? null : (
      <div className="flex flex-col gap-3">
        <p className="text-(length:--text-sm)">
          Mande este código do WhatsApp que você quer ligar para o número do
          assistente, <strong>{data.assistantPhone}</strong>, até{' '}
          {formatDayTime(code.expiresAt)}:
        </p>
        <p
          className="font-(--font-mono) text-(length:--text-2xl) font-(--weight-semibold) tracking-widest"
          aria-label={`Código ${code.code}`}
        >
          AERIS {code.code}
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            className="inline-flex items-center rounded-(--radius-md) bg-(--brand-primary) px-3 py-2 text-(length:--text-sm) font-(--weight-semibold) text-(--text-on-brand)"
            href={code.waMeUrl}
            target="_blank"
            rel="noreferrer"
          >
            Abrir o WhatsApp com o código
          </a>
          <Button
            variant="secondary"
            size="sm"
            loading={issue.isPending}
            onClick={() => {
              issue.mutate(newName?.trim() ?? '')
            }}
          >
            Gerar outro código
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCode(null)
              setNewName(null)
            }}
          >
            Cancelar
          </Button>
        </div>
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Esta tela atualiza sozinha quando o código chegar.
        </p>
      </div>
    )

  return (
    <Section
      title="WhatsApp"
      description="Pergunte os números da loja pelo WhatsApp do assistente e receba o resumo do dia. Ele responde só o que o seu acesso no Aeris já vê."
    >
      <QueryState
        isPending={status.isPending}
        isError={status.isError}
        error={status.error}
        onRetry={() => void status.refetch()}
        what="o seu WhatsApp no assistente"
        isEmpty={false}
        empty={null}
      >
        {data === undefined ? null : !data.available ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            O assistente ainda não está ligado nesta loja. O dono liga em
            Configurações → Assistente.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {links.length === 0 ? (
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                Seu WhatsApp ainda não está ligado.
              </p>
            ) : (
              <>
                <p className="text-(length:--text-sm)">
                  Pergunte pelo número <strong>{data.assistantPhone}</strong>.
                </p>
                <ul
                  className="flex flex-col gap-3"
                  aria-label="Números ligados"
                >
                  {links.map((link) => (
                    <li
                      key={link.id}
                      className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) p-3"
                    >
                      <p className="text-(length:--text-sm)">
                        <strong>{link.userName ?? 'Sem nome'}</strong>
                        {' — '}WhatsApp {link.phone ?? 'deste aparelho'}, ligado
                        desde {formatDayTime(link.linkedAt)}.
                      </p>
                      {renaming?.id === link.id ? (
                        <form
                          className="flex flex-wrap items-end gap-2"
                          onSubmit={(event) => {
                            event.preventDefault()
                            if (nameError(renaming.name) !== undefined) return
                            rename.mutate({
                              linkId: link.id,
                              name: renaming.name,
                            })
                          }}
                        >
                          <Field
                            label="Nome de quem usa este WhatsApp"
                            required
                            error={nameError(renaming.name)}
                          >
                            {(field) => (
                              <Input
                                {...field}
                                value={renaming.name}
                                className="w-64"
                                onChange={(event) => {
                                  setRenaming({
                                    id: link.id,
                                    name: event.target.value,
                                  })
                                }}
                              />
                            )}
                          </Field>
                          <Button
                            type="submit"
                            size="sm"
                            loading={rename.isPending}
                          >
                            Salvar o nome
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setRenaming(null)
                            }}
                          >
                            Cancelar
                          </Button>
                        </form>
                      ) : null}
                      <SwitchField
                        label="Receber o resumo do dia"
                        description={
                          data.digestTimes.length === 0
                            ? 'O dono ainda não marcou horários.'
                            : `Às ${data.digestTimes.map(hourLabel).join(', ')}, no relógio da loja.`
                        }
                        checked={link.receivesDigest}
                        disabled={digest.isPending}
                        onCheckedChange={(checked) => {
                          digest.mutate({ linkId: link.id, on: checked })
                        }}
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRenaming({
                              id: link.id,
                              name: link.personName ?? link.userName ?? '',
                            })
                          }}
                        >
                          Trocar o nome
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setUnlinking(link)
                          }}
                        >
                          Desligar este número
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {codePanel ??
              (full ? (
                <p className="text-(length:--text-xs) text-(--text-muted)">
                  {`Até ${String(max)} números por pessoa. Para ligar outro, desligue um.`}
                </p>
              ) : newName === null ? (
                <div>
                  <Button
                    size="sm"
                    variant={links.length === 0 ? 'primary' : 'secondary'}
                    onClick={() => {
                      setNewName('')
                      setNewNameTouched(false)
                    }}
                  >
                    {links.length === 0
                      ? 'Ligar meu WhatsApp'
                      : 'Ligar outro número'}
                  </Button>
                </div>
              ) : (
                <form
                  className="flex flex-wrap items-end gap-2"
                  onSubmit={(event) => {
                    event.preventDefault()
                    setNewNameTouched(true)
                    if (nameError(newName) !== undefined) return
                    issue.mutate(newName.trim())
                  }}
                >
                  <Field
                    label="Nome de quem vai usar este WhatsApp"
                    required
                    hint="O assistente chama a pessoa por este nome."
                    error={newNameTouched ? nameError(newName) : undefined}
                  >
                    {(field) => (
                      <Input
                        {...field}
                        value={newName}
                        className="w-64"
                        onChange={(event) => {
                          setNewName(event.target.value)
                        }}
                      />
                    )}
                  </Field>
                  <Button type="submit" size="sm" loading={issue.isPending}>
                    Gerar o código
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setNewName(null)
                    }}
                  >
                    Cancelar
                  </Button>
                </form>
              ))}
          </div>
        )}
      </QueryState>

      <ConfirmDialog
        open={unlinking !== null}
        title="Desligar este WhatsApp do assistente?"
        description={`${unlinking?.phone ?? 'Este número'} para de receber o resumo do dia, e o assistente não responde mais ele. Os outros números continuam ligados.`}
        confirmLabel="Desligar"
        tone="danger"
        loading={unlink.isPending}
        onConfirm={async () => {
          if (unlinking !== null) await unlink.mutateAsync(unlinking.id)
        }}
        onOpenChange={(open) => {
          if (!open) setUnlinking(null)
        }}
      />
    </Section>
  )
}
